import React from "react";
import { styled } from "@mui/material/styles";

const Root = styled("div")({
  display: "flex",
  flexShrink: 0,
  position: "relative",
});
const Content = styled("div")({
  height: "100%",
  overflow: "hidden",
  width: "100%",
});
/**
 * How far the drag target reaches over each side of the panel's right edge.
 *
 * Every panel in the app sits against a 1px vertical `Divider` its tab draws just past that edge —
 * drawn by the tab rather than here because it stays when the panel collapses to a rail and this
 * handle is hidden. That divider is the seam's line at rest. The handle lies over it rather than
 * beside it: a strip of its own (#5816) put a 16px gap between the panel and the divider, with a
 * second rule at the near side of the gap.
 *
 * Kept this narrow because whatever it lies over loses its clicks to it. A 16px target straddling
 * the edge swallowed the fee table's leftmost 8px (#5258); held inside the panel, its rightmost
 * 16px, with the Download button that ends every Conflicts and Policies row and the template
 * table's scrollbar (#5816). Three pixels of either is the scrollbar's outer edge and the padding
 * the panel beside starts with.
 */
const HANDLE_OVERHANG = 3;
const DIVIDER_WIDTH = 1;
/**
 * The rule drawn over the divider while the handle holds the pointer or focus, one pixel past it on
 * each side. At rest nothing is drawn: `divider` is translucent — `rgba(0, 0, 0, 0.12)` — so a rule of
 * the same colour laid over the tab's divider would read darker than the divider does anywhere else.
 */
const RULE_HOVER_WIDTH = DIVIDER_WIDTH + 2;
/**
 * One column of dots, drawn here rather than taken from an icon, with the same space either side.
 *
 * Slim, so the grip ends well short of the text the panel beside starts with — at 13px it stood
 * against the Vendor Policies heading. An icon's dots land on fractional pixels at any size this
 * small (`MoreVert` at 16px puts them 2.67px wide), so the space either side came out 2px and 1px;
 * dots of a whole pixel size in a box of whole pixels leave equal space on both sides.
 */
const GRIP_DOT_SIZE = 2;
const GRIP_DOT_GAP = 2;
const GRIP_DOT_INSET = 2;
const GRIP_BORDER_WIDTH = 1;
const GRIP_WIDTH = GRIP_DOT_INSET + GRIP_DOT_SIZE + GRIP_DOT_INSET + GRIP_BORDER_WIDTH;

const Handle = styled("div")(({ theme }) => ({
  bottom: 0,
  cursor: "ew-resize",
  left: `calc(100% - ${HANDLE_OVERHANG}px)`,
  outline: 0,
  position: "absolute",
  top: 0,
  touchAction: "none",
  width: HANDLE_OVERHANG * 2,
  // Above the panel beside, which comes later in the row and would otherwise paint over the grip.
  zIndex: 3,
  "&::after": {
    backgroundColor: "transparent",
    bottom: 0,
    content: '""',
    left: HANDLE_OVERHANG - 1,
    position: "absolute",
    top: 0,
    transition: "background-color 0.15s ease",
    width: RULE_HOVER_WIDTH,
    // Only earns its keep mid-transition, while the grip is still stepping aside.
    zIndex: 2,
  },
  "&:hover::after, &:focus-visible::after": {
    backgroundColor: theme.palette.primary.main,
  },
  "&:hover .ResizablePanel-grip, &:focus-visible .ResizablePanel-grip": {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    left: HANDLE_OVERHANG - 1 + RULE_HOVER_WIDTH,
  },
}));
const Grip = styled("span")(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
  border: `${GRIP_BORDER_WIDTH}px solid ${theme.palette.divider}`,
  // Square against the divider and rounded away from it, with no border at all on the divider's side:
  // the divider is that edge, and a border there would only be a second line beside the first.
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: theme.shape.borderRadius,
  borderLeftWidth: 0,
  borderTopLeftRadius: 0,
  borderTopRightRadius: theme.shape.borderRadius,
  color: theme.palette.text.secondary,
  display: "flex",
  flexDirection: "column",
  gap: GRIP_DOT_GAP,
  height: 32,
  justifyContent: "center",
  // Beside the divider rather than beneath it, so the line keeps one weight. It steps aside by the
  // pixel the hover rule reaches past the divider, over the same 150ms, so the two stay touching.
  left: HANDLE_OVERHANG + DIVIDER_WIDTH,
  position: "absolute",
  top: "50%",
  transform: "translate(0, -50%)",
  transition: "border-color 0.15s ease, color 0.15s ease, left 0.15s ease",
  // Reaches past the handle, so it takes the pointer itself: the grip is what a reader aims for, and
  // the handle under it is only six pixels wide. A press on it reaches the handle as its child's.
  width: GRIP_WIDTH,
  zIndex: 1,
}));
const GripDot = styled("span")({
  backgroundColor: "currentColor",
  borderRadius: "50%",
  flexShrink: 0,
  height: GRIP_DOT_SIZE,
  width: GRIP_DOT_SIZE,
});

/**
 * How far past the minimum a drag has to go before it means "put the panel away" rather than "as
 * narrow as it goes". Far enough that a drag aimed at the minimum does not overshoot into it.
 */
export const SNAP_TO_RAIL_DISTANCE = 64;

type ResizablePanelProps = React.PropsWithChildren<{
  className?: string;
  initialWidth?: number;
  minWidth?: number;
  minRemainingWidth?: number;
  /**
   * Hears the width the reader settled on: once a drag is released, or after each arrow-key step.
   * Not on every move of a drag, so a caller that saves it saves once per gesture.
   */
  onWidthCommitted?: (width: number) => void;
  /**
   * Offered, a drag carried {@link SNAP_TO_RAIL_DISTANCE} past the minimum asks for the panel to be
   * put away, and the drag ends there. The panel keeps the width the drag started from, for when it
   * comes back.
   */
  onSnapToRail?: () => void;
}>;

export default function ResizablePanel(props: Readonly<ResizablePanelProps>) {
  const { children, className, onSnapToRail, onWidthCommitted } = props;
  const minWidth = props.minWidth ?? 375;
  // A panel opens at its narrowest. Templates and document selectors are pickers and the tab's own
  // editor is the work, so the room belongs to the editor until a reader drags some of it back —
  // which is one gesture, where recovering the width an over-wide default took was not.
  const initialWidth = props.initialWidth ?? minWidth;
  const minRemainingWidth = props.minRemainingWidth ?? 320;

  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const handleRef = React.useRef<HTMLDivElement | null>(null);
  const pointerIdRef = React.useRef<number | null>(null);
  const startXRef = React.useRef(0);
  const startWidthRef = React.useRef(initialWidth);
  const widthRef = React.useRef(initialWidth);

  const applyWidth = React.useCallback((nextWidth: number) => {
    widthRef.current = nextWidth;
    if (rootRef.current) {
      rootRef.current.style.width = `${nextWidth}px`;
    }
  }, []);

  const getMaxWidth = React.useCallback(() => {
    if (!rootRef.current?.parentElement) return Number.POSITIVE_INFINITY;

    const parentRect = rootRef.current.parentElement.getBoundingClientRect();
    const rootRect = rootRef.current.getBoundingClientRect();
    const siblingsWidth = Array.from(rootRef.current.parentElement.children)
      .filter((element) => element !== rootRef.current)
      .reduce((totalWidth, element) => {
        const styles = globalThis.getComputedStyle(element);
        const minWidthValue = Number.parseFloat(styles.minWidth);
        const flexGrow = Number.parseFloat(styles.flexGrow);
        let reservedWidth = (element).getBoundingClientRect().width;

        if (Number.isFinite(minWidthValue) && minWidthValue > 0) {
          reservedWidth = minWidthValue;
        } else if (Number.isFinite(flexGrow) && flexGrow > 0) {
          reservedWidth = 0;
        }

        const marginLeft = Number.parseFloat(styles.marginLeft);
        const marginRight = Number.parseFloat(styles.marginRight);

        return totalWidth + reservedWidth + marginLeft + marginRight;
      }, 0);

    return Math.max(
      minWidth,
      Math.min(
        parentRect.right - rootRect.left,
        parentRect.width - siblingsWidth - minRemainingWidth,
      ),
    );
  }, [minRemainingWidth, minWidth]);

  const updateHandleRange = React.useCallback(() => {
    if (!handleRef.current) return;

    const maxWidth = getMaxWidth();
    handleRef.current.setAttribute("aria-valuemin", `${minWidth}`);
    handleRef.current.setAttribute("aria-valuenow", `${Math.round(widthRef.current)}`);
    if (Number.isFinite(maxWidth)) {
      handleRef.current.setAttribute("aria-valuemax", `${Math.round(maxWidth)}`);
    } else {
      handleRef.current.removeAttribute("aria-valuemax");
    }
  }, [getMaxWidth, minWidth]);

  React.useLayoutEffect(() => {
    applyWidth(Math.max(initialWidth, minWidth));
    updateHandleRange();
  }, [applyWidth, initialWidth, minWidth, updateHandleRange]);

  const commitWidth = React.useCallback(() => {
    onWidthCommitted?.(widthRef.current);
  }, [onWidthCommitted]);

  const stopDragging = React.useCallback((handleArg?: HTMLDivElement | null) => {
    // Read the fallback in the body rather than as a parameter default: the React Compiler cannot
    // reorder a member expression used as a default value and bails out on the whole component.
    const handle = handleArg === undefined ? handleRef.current : handleArg;
    if (pointerIdRef.current !== null && handle?.hasPointerCapture(pointerIdRef.current)) {
      handle.releasePointerCapture(pointerIdRef.current);
    }
    pointerIdRef.current = null;
    document.body.style.removeProperty("cursor");
    document.body.style.removeProperty("user-select");
  }, []);

  const handlePointerDown = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!rootRef.current) return;

    pointerIdRef.current = event.pointerId;
    startXRef.current = event.clientX;
    startWidthRef.current = rootRef.current.getBoundingClientRect().width;

    event.currentTarget.setPointerCapture(event.pointerId);
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
    event.preventDefault();
  }, []);

  const handlePointerMove = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId || !rootRef.current) return;

    const draggedWidth = startWidthRef.current + (event.clientX - startXRef.current);
    if (onSnapToRail && draggedWidth < minWidth - SNAP_TO_RAIL_DISTANCE) {
      // Put away, at the width it had before this drag, for when it comes back.
      applyWidth(startWidthRef.current);
      stopDragging(event.currentTarget);
      onSnapToRail();
      return;
    }
    const nextWidth = Math.min(Math.max(minWidth, draggedWidth), getMaxWidth());
    applyWidth(nextWidth);
    updateHandleRange();
  }, [applyWidth, getMaxWidth, minWidth, onSnapToRail, stopDragging, updateHandleRange]);

  const handleKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 48 : 16;
    let nextWidth: number | undefined;

    if (event.key === "ArrowLeft") nextWidth = widthRef.current - step;
    if (event.key === "ArrowRight") nextWidth = widthRef.current + step;
    if (event.key === "Home") nextWidth = minWidth;
    if (event.key === "End") nextWidth = getMaxWidth();
    if (nextWidth === undefined) return;

    event.preventDefault();
    const previousWidth = widthRef.current;
    applyWidth(Math.min(Math.max(minWidth, nextWidth), getMaxWidth()));
    updateHandleRange();
    // A key at either end moves nothing, so there is no new width to keep (review of #6782).
    if (widthRef.current !== previousWidth) commitWidth();
  }, [applyWidth, commitWidth, getMaxWidth, minWidth, updateHandleRange]);

  React.useEffect(() => {
    const clampToAvailableWidth = () => {
      if (!rootRef.current) return;

      const nextWidth = Math.min(widthRef.current, getMaxWidth());
      // Only write when the answer actually moved: the observer below watches the row this panel is
      // in, and a panel that rewrote its width on every notification could keep feeding itself.
      if (nextWidth === widthRef.current) return;
      applyWidth(nextWidth);
      updateHandleRange();
    };

    /**
     * The row narrows without the window changing size — on a proposal page, opening the comments
     * drawer takes 400px of it — so a panel dragged wide beforehand stayed wide and overflowed the
     * row it no longer fits (#4964). Watching the row itself catches that as well as a window resize.
     */
    const row = rootRef.current?.parentElement;
    let rowObserver: ResizeObserver | undefined;
    if (row && typeof ResizeObserver !== "undefined") {
      rowObserver = new ResizeObserver(clampToAvailableWidth);
      rowObserver.observe(row);
    }

    globalThis.addEventListener("resize", clampToAvailableWidth);
    return () => {
      rowObserver?.disconnect();
      globalThis.removeEventListener("resize", clampToAvailableWidth);
    };
  }, [applyWidth, getMaxWidth, updateHandleRange]);

  React.useEffect(() => () => {
    if (pointerIdRef.current !== null) {
      stopDragging();
    }
  }, [stopDragging]);

  const handlePointerUp = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId) return;
    stopDragging(event.currentTarget);
    if (widthRef.current !== startWidthRef.current) commitWidth();
  }, [commitWidth, stopDragging]);

  const handlePointerCancel = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== event.pointerId) return;
    stopDragging(event.currentTarget);
  }, [stopDragging]);

  return (
    <Root className={className} ref={rootRef}>
      <Content>{children}</Content>
      <Handle
        aria-label="Resize panel"
        aria-orientation="vertical"
        onLostPointerCapture={(event) => {
          if (pointerIdRef.current === event.pointerId) {
            stopDragging(event.currentTarget);
          }
        }}
        onFocus={updateHandleRange}
        onKeyDown={handleKeyDown}
        onPointerDown={handlePointerDown}
        onPointerCancel={handlePointerCancel}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        ref={handleRef}
        role="separator"
        tabIndex={0}
        title="Drag or use the arrow keys to resize"
      >
        <Grip aria-hidden className="ResizablePanel-grip">
          <GripDot />
          <GripDot />
          <GripDot />
        </Grip>
      </Handle>
    </Root>
  );
}
